// src/components/Calendar/Calendar.jsx
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, ImagePlus } from 'lucide-react';
import { getEventStyle, EVENT_TYPE_STYLES } from '../../lib/eventTypes';
import EventDetailModal from './EventDetailModal';
import { calendarDate, eventOccursOn } from '../../utils/ministryDates';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const MAX_VISIBLE = 3;

const Calendar = ({ events = [], isAdmin, onAddEvent, onDeleteEvent }) => {
    const [currentDate, setCurrentDate] = useState(() => calendarDate(new Date()));
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

    const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

    const eventsForDay = (date) =>
        events
            .filter(ev => eventOccursOn(ev, date))
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    // Which event types actually appear this month -> drives the legend.
    const typesThisMonth = [...new Set(
        events
            .filter(ev => {
                const d = calendarDate(ev.startDate);
                return d.getFullYear() === currentDate.getFullYear() && d.getMonth() === currentDate.getMonth();
            })
            .map(ev => (ev.type || 'general').toLowerCase())
            .filter(t => EVENT_TYPE_STYLES[t])
    )];

    const renderCalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const cells = [];

        for (let i = 0; i < firstDay; i++) {
            cells.push(<div key={`empty-${i}`} className="min-h-[5.5rem] sm:min-h-[7.5rem] rounded-xl bg-gray-50/40" />);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const dayEvents = eventsForDay(date);
            const isToday = date.toDateString() === calendarDate(new Date()).toDateString();
            const visible = dayEvents.slice(0, MAX_VISIBLE);
            const overflow = dayEvents.length - visible.length;

            cells.push(
                <div
                    key={day}
                    onClick={() => { if (isAdmin) { setSelectedDate(date); setShowAddModal(true); } }}
                    className={`group relative flex min-h-[5.5rem] flex-col gap-1 rounded-xl border p-1.5 transition-all duration-200 sm:min-h-[7.5rem] sm:p-2
                               ${isToday
                                    ? 'border-purple-300 bg-purple-50/40 ring-1 ring-purple-200'
                                    : 'border-gray-100 bg-white hover:border-purple-100 hover:shadow-sm'}
                               ${isAdmin ? 'cursor-pointer' : ''}`}
                >
                    <div className="flex items-center justify-between">
                        <span className={`grid h-6 w-6 place-items-center rounded-full text-xs font-semibold sm:h-7 sm:w-7 sm:text-sm
                                       ${isToday ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-600'}`}>
                            {day}
                        </span>
                        {isAdmin && (
                            <span className="text-[10px] font-medium text-purple-500 opacity-0 transition-opacity group-hover:opacity-100">
                                + Add
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-1 overflow-hidden">
                        {visible.map((event) => {
                            const style = getEventStyle(event.type);
                            return (
                                <button
                                    key={event.id}
                                    onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); }}
                                    className={`flex items-center gap-1.5 truncate rounded-md px-1.5 py-1 text-left text-[10px] font-medium ring-1 transition-transform hover:scale-[1.02] sm:text-xs ${style.chip}`}
                                    title={event.title}
                                >
                                    <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${style.dot}`} />
                                    <span className="truncate">{event.title}</span>
                                </button>
                            );
                        })}
                        {overflow > 0 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); setSelectedEvent(dayEvents[MAX_VISIBLE]); }}
                                className="px-1.5 text-left text-[10px] font-semibold text-purple-600 hover:underline sm:text-xs"
                            >
                                +{overflow} more
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        return cells;
    };

    return (
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            {/* Header */}
            <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <h2 className="font-display text-xl font-semibold text-gray-900 sm:text-2xl">
                    {MONTHS[currentDate.getMonth()]}{' '}
                    <span className="text-purple-600">{currentDate.getFullYear()}</span>
                </h2>
                <div className="flex items-center gap-1.5">
                    <button onClick={handlePrevMonth} aria-label="Previous month"
                            className="grid h-9 w-9 place-items-center rounded-full text-gray-500 transition-colors hover:bg-purple-50 hover:text-purple-700">
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button onClick={() => setCurrentDate(calendarDate(new Date()))}
                            className="rounded-full px-4 py-1.5 text-sm font-semibold text-purple-700 transition-colors hover:bg-purple-50">
                        Today
                    </button>
                    <button onClick={handleNextMonth} aria-label="Next month"
                            className="grid h-9 w-9 place-items-center rounded-full text-gray-500 transition-colors hover:bg-purple-50 hover:text-purple-700">
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Legend */}
            {typesThisMonth.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-gray-100 px-4 py-2.5 sm:px-6">
                    {typesThisMonth.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium capitalize text-gray-500">
                            <span className={`h-2 w-2 rounded-full ${EVENT_TYPE_STYLES[t].dot}`} />
                            {EVENT_TYPE_STYLES[t].label}
                        </span>
                    ))}
                </div>
            )}

            {/* Grid */}
            <div className="p-2 sm:p-4">
                <div className="mb-2 grid grid-cols-7 gap-1 sm:gap-2">
                    {DAYS.map((day, index) => (
                        <div key={index} className="text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400 sm:text-xs">
                            <span className="sm:hidden">{SHORT_DAYS[index]}</span>
                            <span className="hidden sm:inline">{day}</span>
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-1 sm:gap-2">
                    {renderCalendarDays()}
                </div>
            </div>

            {/* Add Event Modal (admin) */}
            {showAddModal && selectedDate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-purple-950/60 p-3 backdrop-blur-sm sm:p-4" onClick={() => setShowAddModal(false)}>
                    <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
                            <h3 className="font-display text-lg font-semibold text-gray-900">Add Event</h3>
                            <button onClick={() => setShowAddModal(false)} className="grid h-9 w-9 place-items-center rounded-full text-gray-400 transition-all hover:rotate-90 hover:bg-gray-100">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                const fd = new FormData(e.target);
                                const imageFile = fd.get('image');
                                try {
                                    await onAddEvent({
                                        title: fd.get('title'),
                                        description: fd.get('description'),
                                        startDate: fd.get('startDate'),
                                        dateLabel: `${fd.get('startDate')} — time not specified`,
                                        endDate: fd.get('endDate') || null,
                                        location: fd.get('location'),
                                        type: fd.get('type'),
                                        image: imageFile && imageFile.size > 0 ? imageFile : null,
                                    });
                                    setShowAddModal(false);
                                } catch (err) {
                                    console.error('Error adding event:', err);
                                    alert('Failed to add event');
                                }
                            }}
                            className="space-y-4 p-6"
                        >
                            <Field label="Title">
                                <input name="title" type="text" required placeholder="Event title" className={inputCls} />
                            </Field>
                            <Field label="Description">
                                <textarea name="description" required rows={3} placeholder="What's happening?" className={inputCls} />
                            </Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Start date">
                                    <input name="startDate" type="date" required defaultValue={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`} className={inputCls} />
                                </Field>
                                <Field label="End date">
                                    <input name="endDate" type="date" className={inputCls} />
                                </Field>
                            </div>
                            <Field label="Location">
                                <input name="location" type="text" required placeholder="Where?" className={inputCls} />
                            </Field>
                            <Field label="Type">
                                <select name="type" defaultValue="event" className={inputCls}>
                                    <option value="event">Event</option>
                                    <option value="meeting">Meeting</option>
                                    <option value="celebration">Celebration</option>
                                    <option value="fundraiser">Fundraiser</option>
                                    <option value="deadline">Deadline</option>
                                    <option value="general">General</option>
                                </select>
                            </Field>
                            <Field label="Image (optional)">
                                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-500 transition-colors hover:border-purple-300 hover:text-purple-600">
                                    <ImagePlus className="h-4 w-4" />
                                    <span>Choose an image…</span>
                                    <input type="file" name="image" accept="image/*" className="sr-only"
                                           onChange={(e) => {
                                               const f = e.target.files?.[0];
                                               e.target.parentElement.querySelector('span').textContent = f ? f.name : 'Choose an image…';
                                           }} />
                                </label>
                            </Field>
                            <button type="submit" className="w-full rounded-lg bg-purple-600 px-4 py-2.5 font-semibold text-white transition-colors hover:bg-purple-700">
                                Add Event
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Event Details Modal */}
            <EventDetailModal
                event={selectedEvent}
                onClose={() => setSelectedEvent(null)}
                isAdmin={isAdmin}
                onDelete={onDeleteEvent}
            />
        </div>
    );
};

const inputCls = 'w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-purple-400 focus:ring-2 focus:ring-purple-100';

const Field = ({ label, children }) => (
    <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
        {children}
    </div>
);

export default Calendar;
